"use client";

import { useEffect, useState } from "react";
import RedirectsCard from "./RedirectsCard";
import AnalyzeResultsModal from "./AnalyzeResultsModal";
import type {
  StoryblokRedirect,
  StoryblokRedirectInput,
  RedirectValidationErrors,
} from "@/types/redirects";

const REDIRECT = {
  oldPath: "",
  targetPage: {
    cached_url: "",
    linktype: "story",
    fieldtype: "multilink",
    id: "",
  },
  statusCode: "308",
};

const RedirectsConfig = () => {
  const [search, setSearch] = useState("");
  const [redirects, setRedirects] = useState<StoryblokRedirect[]>([]);
  const [showStagingModal, setShowStagingModal] = useState(false);
  const [stagedRedirects, setStagedRedirects] = useState<StoryblokRedirect[]>(
    [],
  );

  const [foundIssues, setFoundIssues] = useState<{
    duplicates: Record<string, StoryblokRedirect[]>;
    chains: Record<string, StoryblokRedirect[]>;
    loops: Record<string, StoryblokRedirect>;
  }>({
    duplicates: {},
    chains: {},
    loops: {},
  });

  const [filteredRedirects, setFilteredRedirects] = useState<
    StoryblokRedirect[]
  >([]);
  const [newRedirect, setNewRedirect] =
    useState<StoryblokRedirectInput>(REDIRECT);
  const [validationErrors, setValidationErrors] =
    useState<RedirectValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateRedirect = (
    redirect: StoryblokRedirectInput,
    allRedirects: StoryblokRedirect[],
    skipUid?: string,
  ) => {
    const errors: Record<string, string> = {};

    if (!redirect.oldPath?.trim()) {
      errors.oldPath = "Source path is required";
    } else {
      // Normalize path for comparison (ensure it starts with /)
      const normalizeUrl = (url: string) => {
        if (!url) return "";
        return url.startsWith("/") ? url : `/${url}`;
      };

      // Check for duplicates
      const normalizedFromPath = normalizeUrl(redirect.oldPath);
      const duplicate = allRedirects.find(
        (r) =>
          r._uid !== skipUid && normalizeUrl(r.oldPath) === normalizedFromPath,
      );

      if (duplicate) {
        console.log("Duplicate found:", {
          current: normalizedFromPath,
          existing: normalizeUrl(duplicate.oldPath),
          skipUid,
          currentUid: redirect._uid,
          duplicateUid: duplicate._uid,
        });
        errors.oldPath = "Duplicate source path already exists";
      }
    }

    if (!redirect.targetPage?.cached_url?.trim()) {
      errors.targetPage = "Destination path is required";
    }

    // Check if From and To are the same
    if (redirect.oldPath?.trim() && redirect.targetPage?.cached_url?.trim()) {
      const normalizeUrl = (url: string) => {
        if (!url) return "";
        return url.startsWith("/") ? url : `/${url}`;
      };

      const normalizedFromPath = normalizeUrl(redirect.oldPath);
      const normalizedToPath = normalizeUrl(redirect.targetPage.cached_url);

      if (normalizedFromPath === normalizedToPath) {
        errors.oldPath = "Source and destination paths cannot be the same";
      }
    }

    return errors;
  };

  const handleAddRedirect = async () => {
    if (!newRedirect.oldPath || !newRedirect.targetPage) return;

    // Use stagedRedirects for validation to include current changes
    const errors = validateRedirect(newRedirect, stagedRedirects);
    if (Object.keys(errors).length > 0) {
      setValidationErrors({ new: Object.values(errors).join(", ") });
      return;
    }

    setIsLoading(true);
    setValidationErrors({});

    try {
      const response = await fetch(`/api/redirects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ redirect: newRedirect }),
      });

      if (response.ok) {
        const stories = await response.json();
        setRedirects(stories);
        setStagedRedirects(stories);
        setNewRedirect(REDIRECT);
      } else {
        const errorText = await response.text();
        console.error("API Error:", errorText);
        setValidationErrors({ new: "Failed to save redirect" });
      }
    } catch (error) {
      console.error("Error adding redirect:", error);
      setValidationErrors({ new: "Failed to save redirect" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetch(`/api/redirects`)
      .then((data) => data.json())
      .then((stories) => {
        setRedirects(stories);
        setStagedRedirects(stories);
      });
  }, []);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    setFilteredRedirects(
      q
        ? redirects.filter(
            (redirect) =>
              redirect.oldPath?.toLowerCase().includes(q) ||
              redirect.targetPage?.cached_url?.toLowerCase().includes(q),
          )
        : redirects,
    );

    const foundIssues = analyzeRedirects(redirects);

    setFoundIssues(foundIssues);
  }, [redirects, search]);

  const updateRedirect = (
    _uid: string,
    source: string,
    destination: string,
    statusCode: string,
    uuid?: string,
  ) => {
    // Clear validation errors for this redirect when updating
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[_uid];
      return newErrors;
    });

    const newStagedRedirects = stagedRedirects.map((redirect) => {
      if (redirect._uid === _uid) {
        return {
          ...redirect,
          oldPath: source,
          targetPage: {
            ...redirect.targetPage,
            cached_url: destination,
            id: uuid || "",
          },
          statusCode,
        };
      }

      return redirect;
    });

    setStagedRedirects(newStagedRedirects);
  };

  const saveRedirect = async (
    _uid: string,
    source: string,
    destination: string,
    statusCode: string,
  ) => {
    const redirect = {
      _uid,
      oldPath: source,
      targetPage: {
        cached_url: destination,
        fieldtype: "multilink",
        linktype: "story",
        id: "",
      },
      statusCode,
    };

    // Use stagedRedirects for validation to include current changes
    const errors = validateRedirect(redirect, stagedRedirects, _uid);
    if (Object.keys(errors).length > 0) {
      setValidationErrors({ [_uid]: Object.values(errors).join(", ") });
      return;
    }

    setIsLoading(true);
    setValidationErrors({});

    try {
      const response = await fetch(`/api/redirects`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ redirect }),
      });

      if (response.ok) {
        const stories = await response.json();
        setRedirects(stories);
        setStagedRedirects(stories);
      } else {
        const errorText = await response.text();
        console.error("API Error:", errorText);
        setValidationErrors({ [_uid]: "Failed to save redirect" });
      }
    } catch (error) {
      console.error("Error saving redirect:", error);
      setValidationErrors({ [_uid]: "Failed to save redirect" });
    } finally {
      setIsLoading(false);
    }
  };

  const updateNewRedirect = (
    _uid: string,
    source: string,
    destination: string,
    statusCode: string,
    uuid?: string,
  ) => {
    // Clear validation errors for new redirect when updating
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.new;
      return newErrors;
    });

    setNewRedirect({
      oldPath: source,
      targetPage: {
        cached_url: destination,
        id: uuid || "",
        linktype: "story",
        fieldtype: "multilink",
      },
      statusCode,
    });
  };

  const deleteRedirect = async (_uid: string) => {
    setIsLoading(true);
    setValidationErrors({});

    try {
      const response = await fetch(`/api/redirects?redirect=${_uid}`, {
        method: "DELETE",
      });

      if (response.ok) {
        const stories = await response.json();
        setRedirects(stories);
        setStagedRedirects(stories);
      } else {
        setValidationErrors({ [_uid]: "Failed to delete redirect" });
      }
    } catch (error) {
      console.error("Error deleting redirect:", error);
      setValidationErrors({ [_uid]: "Failed to delete redirect" });
    } finally {
      setIsLoading(false);
    }
  };

  const undoChanges = (_uid: string) => {
    const original = redirects.find((r) => r._uid === _uid);
    if (!original) return;

    const exists = stagedRedirects.some((r) => r._uid === _uid);
    if (exists) {
      setStagedRedirects(
        stagedRedirects.map((r) => (r._uid === _uid ? original : r)),
      );
      return;
    }

    const originalIndex = redirects.findIndex((r) => r._uid === _uid);
    let insertAt = stagedRedirects.length;
    for (let i = originalIndex - 1; i >= 0; i--) {
      const prev = redirects[i];
      if (!prev) continue;
      const stagedIdx = stagedRedirects.findIndex((s) => s._uid === prev._uid);
      if (stagedIdx !== -1) {
        insertAt = stagedIdx + 1;
        break;
      }
      if (i === 0) insertAt = 0;
    }
    const next = [...stagedRedirects];
    next.splice(insertAt, 0, original);
    setStagedRedirects(next);
  };

  return (
    <div className="relative py-8 w-[800px] mx-auto">
      <input
        className="w-full p-2 border border-gray-300 rounded-md mb-6"
        placeholder="Search redirects..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="p-6 border border-gray-200 rounded-lg mb-8 flex gap-4 justify-between">
        <RedirectsCard
          redirect={newRedirect}
          updateRedirect={updateNewRedirect}
          disabledButtons
          validationError={validationErrors.new}
          isLoading={isLoading}
        />
        <div className="flex flex-col gap-4 w-[145px]">
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleAddRedirect}
            disabled={isLoading}
          >
            {isLoading ? "Adding..." : "Add Redirect"}
          </button>
        </div>
      </div>

      {showStagingModal ? (
        <AnalyzeResultsModal
          foundIssues={foundIssues}
          closeModal={() => setShowStagingModal(false)}
          updateRedirect={updateRedirect}
          deleteRedirect={deleteRedirect}
          undoChanges={undoChanges}
          stagedRedirects={stagedRedirects}
          saveRedirect={saveRedirect}
        />
      ) : (
        <div>
          {Boolean(
            Object.values(foundIssues.duplicates).length ||
              Object.values(foundIssues.duplicates).length ||
              Object.values(foundIssues.duplicates).length,
          ) && (
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
              onClick={() => setShowStagingModal(!showStagingModal)}
            >
              Show issues
            </button>
          )}

          {filteredRedirects.length === 0 ? (
            <p className="text-gray-500 m-2">No redirects found.</p>
          ) : (
            <p className="text-gray-500 m-2">
              Total: {filteredRedirects.length}
            </p>
          )}

          {filteredRedirects.map((r) => {
            const stagedRedirect = stagedRedirects.find(
              (redirect) => redirect._uid === r._uid,
            );

            if (stagedRedirect) {
              const isModified =
                JSON.stringify(stagedRedirect) !== JSON.stringify(r);

              return (
                <RedirectsCard
                  redirect={stagedRedirect}
                  updateRedirect={updateRedirect}
                  deleteRedirect={deleteRedirect}
                  undoChanges={undoChanges}
                  saveRedirect={saveRedirect}
                  key={r._uid}
                  isModified={isModified}
                  validationError={validationErrors[r._uid]}
                  isLoading={isLoading}
                />
              );
            }

            return (
              <RedirectsCard
                redirect={r}
                key={r._uid}
                isDeleted
                undoChanges={undoChanges}
                validationError={validationErrors[r._uid]}
                isLoading={isLoading}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RedirectsConfig;

// Simple hash function for generating unique keys from strings
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(36);
}

function analyzeRedirects(redirects: StoryblokRedirect[]): {
  duplicates: Record<string, StoryblokRedirect[]>;
  chains: Record<string, StoryblokRedirect[]>;
  loops: Record<string, StoryblokRedirect>;
} {
  const foundIssues: {
    duplicates: Record<string, StoryblokRedirect[]>;
    chains: Record<string, StoryblokRedirect[]>;
    loops: Record<string, StoryblokRedirect>;
  } = { duplicates: {}, chains: {}, loops: {} };

  try {
    for (let i = 0; i < redirects.length; i++) {
      const currentRedirect = redirects[i];
      if (!currentRedirect) continue;

      const duplicates = [currentRedirect];

      for (let j = 0; j < redirects.length; j++) {
        const otherRedirect = redirects[j];
        if (
          !otherRedirect ||
          i === j ||
          Object.keys(foundIssues.duplicates).some(
            (d) => d === otherRedirect.oldPath,
          )
        ) {
          continue;
        }

        if (currentRedirect.oldPath === otherRedirect.oldPath) {
          duplicates.push(otherRedirect);
        }
      }

      if (duplicates.length > 1) {
        foundIssues.duplicates[currentRedirect.oldPath] = duplicates;
      }
    }

    for (const redirect of redirects) {
      const chain = getRedirectChain(redirect, redirects);

      const hash = simpleHash(JSON.stringify(chain));

      if (chain.length > 1 && !foundIssues.chains[hash]) {
        foundIssues.chains[hash] = chain;
      }
    }

    for (const redirect of redirects) {
      if (redirect.oldPath === redirect.targetPage.cached_url) {
        foundIssues.loops[redirect.oldPath] = redirect;
      }
    }
  } catch (error) {
    console.log(error);
  }

  return foundIssues;
}

function getRedirectChain(
  redirect: StoryblokRedirect,
  redirects: StoryblokRedirect[],
  chain: StoryblokRedirect[] = [redirect],
) {
  const nextRedirect = redirects.find(
    (r) => r.oldPath === redirect.targetPage.cached_url,
  );

  if (nextRedirect) {
    chain.push(nextRedirect);

    return getRedirectChain(nextRedirect, redirects, chain);
  }

  return chain;
}
